import { ProjectRepresentative, ReportMetadata } from '../../types';
import { getRepresentatives, istPlanungsVerantwortlicher } from '../../services/projectRepresentatives';

/**
 * Begehungstage je Person in den Projektkennzahlen (ProjectStats, Block
 * „Wochenplan & Ressourcen-Split") — ohne React, damit testbar.
 *
 * Warum es diese Datei gibt: Die Verantwortliche Planungsphase steht in
 * derselben Vertreterliste wie die Stellvertreter (`bereich: 'planung'`),
 * macht aber keine Begehungen. „Stellvertreter 1/2" im Wochenplan sind
 * deshalb die ersten beiden AUSFÜHRUNGS-Vertreter, nicht `reps[1]`/`reps[2]`.
 * Geschrieben wird aber über die VOLLE Liste — wer dort nach dem Filtern mit
 * den gefilterten Positionen schreibt, setzt Tage auf die Planungsperson oder
 * wirft sie beim Speichern aus der Liste.
 */

/** Ein Ausführungs-Stellvertreter mit seiner Position in der VOLLEN Liste. */
export interface AusfuehrungsSlot {
    rep: ProjectRepresentative;
    /** Index in `getRepresentatives(meta)` — genau dorthin wird zurückgeschrieben. */
    index: number;
}

/**
 * Stellvertreter ab Position 1 ohne Planungs-Verantwortliche, mit Original-
 * position. Dieselbe Regel wie `getAusfuehrungsVertreter(meta).slice(1)`;
 * eigene Ableitung nur, weil dort der Index verloren geht.
 */
export const ausfuehrungsSlots = (alle: ReadonlyArray<ProjectRepresentative>): AusfuehrungsSlot[] =>
    alle
        .map((rep, index) => ({ rep, index }))
        .filter(({ rep, index }) => index > 0 && !istPlanungsVerantwortlicher(rep));

/** Wie `ausfuehrungsSlots`, direkt aus den Metadaten. */
export const ausfuehrungsStellvertreter = (meta: ReportMetadata | undefined | null): AusfuehrungsSlot[] =>
    ausfuehrungsSlots(getRepresentatives(meta));

export type BegehungstagPerson = 'owner' | 'deputy' | 'deputy2';

export interface BegehungstagAufteilung {
    /** Neue Tage des Hauptverantwortlichen (`intervalConfig.preferredWeekdays`); fehlt = unverändert. */
    ownerDays?: number[];
    /** Volle Vertreterliste mit neuen Tagen; fehlt = Liste nicht schreiben. */
    reps?: ProjectRepresentative[];
}

const ohne = (tage: number[], tag: number): number[] => tage.filter(t => t !== tag);
const sortiert = (tage: number[]): number[] => [...tage].sort((a, b) => a - b);

/**
 * Ein Klick auf einen Wochentag: Ist der Tag bei der Person gesetzt, wird er
 * nur bei ihr entfernt. Sonst wandert er exklusiv zu ihr — Hauptverantwort-
 * licher, Stellvertreter 1 und 2 teilen sich die Woche, kein Tag doppelt.
 *
 * Stellvertreter 1/2 sind die ersten beiden Ausführungs-Vertreter. Alle
 * anderen Einträge, insbesondere Planungs-Verantwortliche, bleiben Objekt
 * für Objekt unverändert an ihrer Stelle — auch ihre Alt-Wochentage.
 */
export const teileBegehungstag = (
    alle: ReadonlyArray<ProjectRepresentative>,
    ownerDays: number[],
    person: BegehungstagPerson,
    day: number,
): BegehungstagAufteilung => {
    const [slot1, slot2] = ausfuehrungsSlots(alle);
    const dep1 = slot1?.rep.weekdays || [];
    const dep2 = slot2?.rep.weekdays || [];

    const mitTagen = (tage1: number[], tage2: number[]): ProjectRepresentative[] =>
        alle.map((rep, idx) => {
            if (slot1 && idx === slot1.index) return { ...rep, weekdays: tage1 };
            if (slot2 && idx === slot2.index) return { ...rep, weekdays: tage2 };
            return rep;
        });

    if (person === 'owner' && ownerDays.includes(day)) return { ownerDays: ohne(ownerDays, day) };
    if (person === 'deputy' && dep1.includes(day)) return { reps: mitTagen(ohne(dep1, day), dep2) };
    if (person === 'deputy2' && dep2.includes(day)) return { reps: mitTagen(dep1, ohne(dep2, day)) };

    const nextOwner = ohne(ownerDays, day);
    const nextDep1 = ohne(dep1, day);
    const nextDep2 = ohne(dep2, day);
    if (person === 'owner') nextOwner.push(day);
    else if (person === 'deputy') nextDep1.push(day);
    else nextDep2.push(day);
    return { ownerDays: sortiert(nextOwner), reps: mitTagen(sortiert(nextDep1), sortiert(nextDep2)) };
};
